/**
 * Edit this file to update site copy. Replace placeholder testimonials
 * and stats when you have real client data.
 */

export const business = {
  name: "ATP Consulting Services",
  shortName: "ATP Consulting",
  tagline:
    "Websites, SEO, and digital marketing for small businesses nationwide",
  phone: "(657) 330-1466",
  email: "info@atpconsultingservices.com",
  linkedin: "https://www.linkedin.com/company/atp-consulting-services",
  serviceArea:
    "Orange County, California — and remotely for small businesses across the United States.",
  consultationNote: "Free initial consultation — no obligation",
};

export const hero = {
  headline:
    "Get Found on Google with a Professional Website for Small Business.",
  subheadline:
    "ATP Consulting Services builds websites, SEO, and digital marketing for shops and local services from Orange County, CA, and remotely nationwide.",
  primaryCta: "Request a Free Consultation",
  secondaryCta: "View Our Services",
};

export const trustPoints = [
  {
    title: "Built for Small Business",
    description:
      "Practical solutions sized to your budget and goals — not one-size-fits-all agency packages.",
  },
  {
    title: "Full-Service Digital Partner",
    description:
      "Websites, SEO, marketing, analytics, and automation under one roof — one team, one plan.",
  },
  {
    title: "Nationwide, with a Personal Team",
    description:
      "In-person in Orange County when it helps; remote work with shops and service businesses anywhere in the U.S.",
  },
  {
    title: "Clear Communication",
    description:
      "Plain-language updates, realistic timelines, and support after launch.",
  },
];

export const servicesLead =
  "Sized to small-business budgets — from a first site and Google visibility through analytics and support after launch.";

export const servicesIncludedNote =
  "Included with website projects: domain & hosting guidance, SSL, basic analytics setup, and linking your social profiles — no need to chase separate vendors for the essentials.";

export const services = [
  {
    title: "Business Consultation and Strategy",
    description:
      "Start with clarity. We assess your goals, budget, and current tools, then recommend what to tackle first so you invest in the right priorities.",
    highlights: [
      "Technology and process assessment",
      "Honest recommendations, without overselling",
      "Roadmap for websites, SEO, and marketing",
    ],
    details:
      "Every engagement starts with a free consultation. We learn your goals, budget, and the tools you already use, then recommend what to tackle first so you invest in the right priorities. You get a technology and process assessment, plain-language recommendations, and a roadmap for the website, SEO, and marketing work that fits a small-business budget. Scope, timeline, and an investment range come before any build.",
  },
  {
    title: "Websites and Online Presence",
    description:
      "Professional sites from simple informational pages to product catalogs and custom features — plus domain, hosting, and launch support handled with you.",
    highlights: [
      "Mobile-friendly design and lead capture",
      "Domain, hosting, and SSL setup",
      "Redesigns and ongoing site improvements",
    ],
    details:
      "Professional sites run from a simple informational presence to product catalogs and custom features. Website projects include domain, hosting, and SSL guidance at launch, a mobile-friendly layout, and a way for customers to call or send a form. You keep ownership of the domain and site. Starter is a first website. Growth adds service pages, galleries, testimonials, Google Maps, and local SEO for shops that need calls and walk-ins. Catalog organizes products or a menu. Custom covers booking, portals, and integrations. We also redesign outdated sites when starting over is the wrong move.",
  },
  {
    title: "SEO and Local Visibility",
    description:
      "Help customers find you on Google with on-page SEO, local search, and a site structure search engines can understand and rank.",
    highlights: [
      "Keyword and competitor research",
      "Local SEO for community businesses",
      "Performance and indexing practices",
    ],
    details:
      "Customers should be able to find you on Google. Work includes keyword and competitor research, on-page SEO, and a site structure search engines can understand and index. Local SEO covers community businesses that need to show up in Search and Maps. For a service-area business, we guide Google Business Profile setup so the listing can show the city and service area without publishing a home street address. Ongoing SEO after launch is optional.",
  },
  {
    title: "Digital Marketing",
    description:
      "Reach the right audience with social, email, content, and paid campaigns — and keep your website and social channels working together.",
    highlights: [
      "Social strategy and profile links",
      "Content and email campaigns",
      "Paid ads setup and guidance",
    ],
    details:
      "Reach the right audience with social, email, content, and paid campaigns, and keep the website and social channels working together. That includes social strategy, profile links, content and email campaigns, and paid ads setup and guidance. Organic search earns visibility over time. Paid ads, such as Google Ads, buy placement. After a consultation we recommend one or both based on your budget and how soon you need leads.",
  },
  {
    title: "Analytics, Automation, and Support",
    description:
      "Measure results, reduce manual work, and get help after launch — from tracking conversions to automating follow-ups and routine tasks.",
    highlights: [
      "Google Analytics and conversion tracking",
      "Workflow automation and tool integration",
      "Insights and support after launch",
    ],
    details:
      "Measure results, reduce manual work, and get help after launch. We set up Google Analytics and conversion tracking, connect the tools you already use, and automate follow-ups and routine tasks. Launch includes training on updates. Optional SEO, marketing, and maintenance stay available so the site remains something you can run.",
  },
];

export const websitePackages = [
  {
    name: "Starter — Informational",
    bestFor: "New businesses, freelancers, and simple online presence.",
    features: [
      "1–5 Pages (Home, About, Services, Contact)",
      "Mobile-Friendly Layout",
      "Contact Form & Business Info",
      "Basic SEO Setup",
    ],
    note: "Ideal when customers mainly need hours, location, and how to reach you.",
    details:
      "A Starter site is the right first website when you need to look established online without a large build. Typical pages are Home, About, Services, and Contact. We set up a mobile-friendly layout, a contact form, your phone and hours, and basic on-page SEO so search engines can understand the business. Domain, hosting, and SSL guidance are included at launch. Add Growth features later if you need galleries, maps, or more service pages.",
  },
  {
    name: "Growth — Service Business",
    bestFor: "Contractors, salons, clinics, restaurants, and local services.",
    features: [
      "Everything in Starter, Plus Expanded Service Pages",
      "Photo Galleries & Testimonials",
      "Google Maps & Local SEO",
      "Social Media Links & Email Signup",
    ],
    note: "Built to generate calls, form leads, and walk-in traffic.",
    details:
      "Growth is for local service businesses that need more than a brochure. You get everything in Starter, plus expanded service pages, photo galleries, testimonials, Google Maps, and local SEO so nearby customers can find you in Search and Maps. Social links and email signup keep the site connected to the channels you already use. This package is built to generate calls, form leads, and walk-in traffic for contractors, salons, clinics, restaurants, and similar shops.",
  },
  {
    name: "Catalog — Products & Menu",
    bestFor: "Retail, bakeries, boutiques, and businesses showcasing items.",
    features: [
      "Product or Menu Display with Categories",
      "High-Quality Image Layouts",
      "Inquiry or Order Request Forms",
      "Optional E-Commerce Integration",
    ],
    note: "Show what you sell online even if checkout is handled in-store or by phone.",
    details:
      "Catalog is for retailers, bakeries, boutiques, and anyone who needs to show products or a menu online. We organize items by category with image-forward layouts and inquiry or order-request forms. Checkout can stay in-store or by phone; e-commerce can be added when you are ready. The goal is a clear catalog customers can browse on a phone before they visit or call.",
  },
  {
    name: "Custom — Full Solution",
    bestFor: "Unique workflows, integrations, or advanced functionality.",
    features: [
      "Custom Features & Third-Party Integrations",
      "Booking, Portals, or Member Areas",
      "Advanced Analytics & Automation",
      "Ongoing Partnership & Support",
    ],
    note: "Pricing scoped after a consultation based on your requirements.",
    featured: true,
    details:
      "Custom is for businesses that need booking flows, client portals, member areas, or third-party integrations that a standard site cannot cover. We scope features, analytics, and automation after a free consultation so the build matches how you actually work. Pricing follows that scope — not a one-size package. After launch we can stay on for support, SEO, and marketing rather than handing off a site you cannot maintain.",
  },
];

export const processSteps = [
  {
    step: "1",
    title: "Free Consultation",
    description:
      "We learn about your business, goals, and budget. You get honest recommendations — not a hard sell.",
  },
  {
    step: "2",
    title: "Proposal & Plan",
    description:
      "You receive a clear scope, timeline, and investment range for the services or website package that fits.",
  },
  {
    step: "3",
    title: "Build & Review",
    description:
      "We design and develop with your feedback at key milestones so the result matches your brand.",
  },
  {
    step: "4",
    title: "Launch & Support",
    description:
      "Go live with training on updates, plus optional ongoing SEO, marketing, and maintenance support.",
  },
];

export const about = {
  headline: "About ATP Consulting Services",
  teaser:
    "ATP Consulting Services is a software engineer-led consultancy based in Orange County, California. We build websites, SEO, and digital marketing for small businesses nationwide — practical work you can maintain after launch.",
  paragraphs: [
    "ATP Consulting Services partners with small businesses nationwide to make technology approachable and profitable. Whether you need your first website, better visibility on Google, or help connecting your tools, we focus on solutions that fit real-world small business budgets — in Orange County, California, and remotely across the United States.",
    "We work with contractors, salons, clinics, restaurants, insurance agencies, retailers, and professional practices that need a clear site, local search, and a team that explains options in plain language.",
    "As a software engineer-led consultancy, we bring technical depth with a personal touch — delivering work you can trust long after launch, not a handoff you cannot maintain.",
  ],
  founderNote:
    "Led by a software engineer with hands-on experience building web applications, SEO-friendly sites, and automation for growing businesses.",
  whoWeServeTitle: "Who We Serve",
  whoWeServeLead:
    "Most of our work is for owner-operated shops and local service brands that need to look established online, rank in Google Search and Maps, and make it easy to call or send a form — without an enterprise agency process.",
  whoWeServe: [
    "Contractors and Home-Service Companies.",
    "Salons, Spas, and Clinics.",
    "Restaurants and Retailers.",
    "Insurance Agencies and Professional Practices.",
  ],
  howWeWorkTitle: "How We Work",
  howWeWorkLead:
    "Every engagement starts with a free consultation. You get a clear scope, timeline, and investment range before we build. Feedback happens at key milestones, and launch includes training plus optional SEO, marketing, and maintenance support.",
  areaTitle: "Where We Work",
  areaLead:
    "We are based in Orange County, California, and meet local clients in person when it helps. Website design, SEO, Google Business Profile, digital marketing, analytics, and automation are also delivered remotely to small businesses across the United States.",
  ownershipNote:
    "You keep ownership of the domain and site. Website projects include domain, hosting, and SSL guidance at launch. Ongoing SEO, marketing, or maintenance is optional after go-live.",
};

export const testimonials = [
  {
    quote:
      "Our new website makes complex insurance look simple. Our clients love how informative it is, and we’ve seen a massive boost in inquiries since launching!",
    name: "I.T.O. Insurance Agency",
  },
  {
    quote:
      "Service and expertise were outstanding from the first call. ATP built us a site that looks as solid as the work we do — homeowners in Houston can find us, understand our services, and reach us without extra friction.",
    name: "Total Garage HTX",
  },
  {
    quote:
      "They treated our salon like it mattered. The expertise showed in the details — branding, layout, and how we show up online — and the service stayed personal the whole way through. Tucson clients now see Starstruck the way we intended.",
    name: "Starstruck Nails & Spa",
  },
];

export const clientWork = [
  {
    name: "I.T.O. Insurance Agency",
    trade: "Insurance",
    line: "Our new website makes complex insurance look simple, and inquiries jumped after launch.",
  },
  {
    name: "Total Garage HTX",
    trade: "Garage organization",
    line: "Homeowners in Houston can find the services and reach the crew without extra friction.",
  },
  {
    name: "Starstruck Nails & Spa",
    trade: "Nail salon",
    line: "Branding, layout, and the way the salon shows up online now match how the business feels.",
  },
];

export const clientIndustries = [
  "Dental",
  "Plumbers",
  "Accountants",
  "Landscapers",
  "Contractors",
  "Hair Salons & Spas",
  "Restaurants",
  "Appliance Warehouses",
  "Garage Cleaning & Organization",
  "Nail Salons",
  "Food Trucks & Catering",
  "Lawyers",
  "Many More",
];

export const faq = [
  {
    category: "Projects & Pricing",
    question: "How much does a website cost?",
    answer:
      "It depends on scope. Starter informational sites are the most affordable; catalog and custom sites require more design and functionality. After a free consultation, we provide a clear quote — no surprise fees.",
  },
  {
    category: "Projects & Pricing",
    question: "How long does a typical project take?",
    answer:
      "Simple sites often launch in a few weeks. Larger projects with custom features or many pages take longer. We agree on a timeline upfront and keep you updated.",
  },
  {
    category: "Projects & Pricing",
    question: "Can you help if we already have a website?",
    answer:
      "Yes. We redesign outdated sites, improve SEO and Google visibility, add new features, and connect marketing tools without starting from scratch when it makes sense.",
  },
  {
    category: "Projects & Pricing",
    question: "What happens with hosting after the site launches?",
    answer:
      "Website projects include domain, hosting, and SSL guidance at launch. You keep ownership of the domain and site. Ongoing SEO, marketing, or maintenance is optional after go-live.",
  },
  {
    category: "Service Area",
    question: "Do you only work with businesses in my area?",
    answer:
      "We are based in Orange County, California, and we work with small businesses nationwide. Local clients can meet in person; everyone else works with us remotely by video, email, and shared project tools.",
  },
  {
    category: "Service Area",
    question: "Can you help a small business anywhere in the United States?",
    answer:
      "Yes. Website design, SEO, Google Business Profile, digital marketing, analytics, and automation are delivered remotely to contractors, salons, clinics, restaurants, retailers, and professional services across the U.S.",
  },
  {
    category: "Service Area",
    question: "What kinds of small businesses do you build websites for?",
    answer:
      "Service businesses, salons, clinics, restaurants, insurance agencies, retailers, and professional practices. Packages cover informational sites, local-service sites, product catalogs, and custom builds with booking or integrations.",
  },
  {
    category: "SEO & Marketing",
    question: "Do you help with Google Business Profile and Maps?",
    answer:
      "Yes. Local SEO work includes Google Business Profile setup and optimization, on-page location details, and site structure that helps customers find you in Google Search and Maps.",
  },
  {
    category: "SEO & Marketing",
    question: "What is the difference between SEO and paid ads?",
    answer:
      "SEO is how your site and listings earn visibility in organic Google results over time. Paid ads (such as Google Ads) buy placement. We can recommend one or both after a consultation, based on your budget and how soon you need leads.",
  },
  {
    category: "SEO & Marketing",
    question: "Do I need to provide photos and copy?",
    answer:
      "We can start from what you have — existing photos, a logo, and notes about your services. During the project we help structure pages, headlines, and calls to action so the site is clear for customers and search engines.",
  },
  {
    category: "Getting in Touch",
    question: "How do I get in touch?",
    answer:
      "Open the Contact page to send a message, use Request a Free Consultation on any page, call (657) 330-1466, or email info@atpconsultingservices.com.",
  },
];

export const faqCategories = [
  {
    id: "projects",
    title: "Projects & Pricing",
    intro:
      "What a website engagement costs, how long it takes, and what happens after launch.",
  },
  {
    id: "service-area",
    title: "Service Area",
    intro:
      "Where we work and which kinds of small businesses we build sites for.",
  },
  {
    id: "seo-marketing",
    title: "SEO & Marketing",
    intro:
      "Google visibility, paid ads, and what you need to provide for the project.",
  },
  {
    id: "getting-in-touch",
    title: "Getting in Touch",
    intro: "How to start a conversation or request a free consultation.",
  },
];

export const faqPageLead =
  "Answers about website cost, project timelines, Orange County and nationwide service, SEO, Google Business Profile, and how to reach ATP Consulting Services.";

export const faqTeaser =
  "Common questions about website cost, timelines, service area, SEO, and how to get in touch — answered in plain language.";
